/**
 * Libraries import
 */
import { lazy, Suspense, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
/**
 * Constants import
 */
import { API, PERMISSIONS, ROUTES } from "../config";
/**
 * Pages/Views/Screen Imports
 */
import { useResetRecoilState } from "recoil";
import {
    bulkConversationsSearchOptions,
    businessesSearchOptions,
    campaignsSearchOptions,
    clientsSearchOptions,
    employeesSearchOptions,
    leadsSearchOptions,
    usersSearchOptions,
} from "../config/recoil/atoms";
import { usePermissions } from "../hooks";
import { resolveTeamHubLanding } from "../containers/teamhub/teamhub-landing";
import { NotFound } from "../views/NotFound";


const LeadsScreen = lazy(() => import("../views/MainScreens/LeadsScreen"));
const LeadsDetailScreen = lazy(() => import("../views/MainScreens/LeadsDetailScreen"));
const SingleLead = lazy(() => import("../views/Single/SingleLead"));
const CreateNewLead = lazy(() => import("../views/Create/CreateGeneric"));

const ClientsScreen = lazy(() => import("../views/MainScreens/ClientsScreen"));
const ClientsDetailScreen = lazy(() => import("../views/MainScreens/ClientsDetailScreen"));
const ActivitySupervision = lazy(() => import("../views/MainScreens/ActivitySupervision"));
const ProductionAudit = lazy(() => import("../views/MainScreens/ProductionAudit"));
const SingleClient = lazy(() => import("../views/Single/SingleClient"));
const CreateNewClient = lazy(() => import("../views/Create/CreateGeneric"));

const EmployeeScreen = lazy(() => import("../views/MainScreens/EmployeeScreen"));
const CreateNewEmployee = lazy(() => import("../views/Create/CreateEmployee"));
const SingleEmployee = lazy(() => import("../views/Single/SingleEmployee"));

const CampaignsScreen = lazy(() => import("../views/MainScreens/Campaigns/CampaignsScreen"));
const SettingsScreen = lazy(() => import("../views/MainScreens/SettingsScreen"));

const DashboardScreen = lazy(() => import("../views/MainScreens/DashboardScreen"));

const ActivityScreen = lazy(() => import("../views/MainScreens/ActivityScreen"));
const SingleActivity = lazy(() => import("../views/Single/SingleActivity"));

const UsersScreen = lazy(() => import("../views/MainScreens/UsersScreen"));
const CreateUser = lazy(() => import("../views/Create/CreateUser"));
const SingleUser = lazy(() => import("../views/Single/SingleUser"));

const EditProfileScreen = lazy(() => import("../views/MainScreens/UserScreens/EditProfileScreen"));

const CalendarScreen = lazy(() => import("../views/MainScreens/CalendarSreen.scene"));
const ConversationScreen = lazy(() => import("../views/MainScreens/ConversationScreen"));
const TutorialsScreen = lazy(() => import("../views/MainScreens/TutorialsScreen"));
const PaymentsScreen = lazy(() => import("../views/MainScreens/PaymentsScreen"));
const SingleCampaign = lazy(() => import("../views/Single/SingleCampaign"));

const BulkConversationScreen = lazy(
    () => import("../views/MainScreens/Campaigns/BulkConversationsScreen")
);
const SingleBulkConversation = lazy(() => import("../views/Single/SingleBulkConversation"));
const OpportunitiesScreen = lazy(() => import("../views/MainScreens/Opportunities/OpportunitiesLayout"));
const AnalyticsScreen = lazy(() => import("../views/MainScreens/AnalyticsScreen"));
const BusinessesScreen = lazy(() => import("../views/MainScreens/BusinessesScreen"));
const SingleBusinessContainer = lazy(() => import("../containers/businesses/single/business.container"));
const CreateBusinessContainer = lazy(() => import("../containers/businesses/create/create-business.container"));
const TeamHubTemplatesScreen = lazy(() => import("../views/MainScreens/TeamHubTemplatesScreen"));
const TeamHubScheduleScreen = lazy(() => import("../views/MainScreens/TeamHubScheduleScreen"));
const TeamHubToolsScreen = lazy(() => import("../views/MainScreens/TeamHubToolsScreen"));
const TeamHubTicketsScreen = lazy(() => import("../views/MainScreens/TeamHubTicketsScreen"));
const RefinanceCashOutContainer = lazy(() => import("../containers/refinance-cash-out/refinance-cash-out.container"));

/**
 * This file lists all the routes within this SPA.
 */
export const ApplicationRoutes = () => {
    const { checkPermission } = usePermissions();

    const teamHubLanding = resolveTeamHubLanding(checkPermission);

    const location = useLocation();
    const resetClientOptions = useResetRecoilState(clientsSearchOptions);
    const resetLeadOptions = useResetRecoilState(leadsSearchOptions);
    const resetEmployeeOptions = useResetRecoilState(employeesSearchOptions);
    const resetCampaignOptions = useResetRecoilState(campaignsSearchOptions);
    const resetuserOptions = useResetRecoilState(usersSearchOptions);
    const resetBulkConversationOptions = useResetRecoilState(bulkConversationsSearchOptions);
    const resetBusinessOptions = useResetRecoilState(businessesSearchOptions);

    useEffect(() => {
        // If the current route in not a clients page, not starting with /client(s),
        // forget the saved search options.
        if (
            !location.pathname.startsWith(ROUTES.CLIENTS) &&
            !location.pathname.startsWith("/appointment")
        )
            resetClientOptions();
        // If the current route in not a leads page, not starting with /lead(s),
        // forget the saved search options.
        if (
            !location.pathname.startsWith(ROUTES.LEADS) &&
            !location.pathname.startsWith("/appointment")
        )
            resetLeadOptions();
        // Same for employees.
        if (!location.pathname.startsWith(ROUTES.EMPLOYEES)) resetEmployeeOptions();
        // The trick doesn't work as well because the base route has 's'
        // while the other don't.
        if (!location.pathname.startsWith("/campaign")) resetCampaignOptions();
        if (!location.pathname.startsWith(ROUTES.BULK_CONVERSATION))
            resetBulkConversationOptions();
        if (!location.pathname.startsWith("/user")) resetuserOptions();
        if (!location.pathname.startsWith("/business")) resetBusinessOptions();
    }, [location]);

    return (
        <Suspense
            fallback={
                <div className="lds-ring absolute inset-1/2 transform translate-x-[-50%] translate-y-[-50%] loading-fade-in">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                </div>
            }
        >
            <Switch>
                {/* The homapage in this app is /dashboard, so when user go to '/' redirect them to
                the dashboard page */}
                <Route exact path={ROUTES.HOME}>
                    <Redirect to={ROUTES.DASHBOARD} from={ROUTES.HOME} />
                </Route>
                {/* TO be defined*/}
                {checkPermission(PERMISSIONS.SETTINGS_ACCESS) && (
                    <Route path={ROUTES.SETTINGS} render={() => <SettingsScreen />} />
                )}
                {/* This route renders the home page */}
                <Route exact path={ROUTES.DASHBOARD} render={() => <DashboardScreen />} />
                {/* This route renders the edit porfile page */}
                <Route exact path={ROUTES.USER_PROFILE} render={() => <EditProfileScreen />} />
                {/* This route renders bulk conversation page */}
                {checkPermission(PERMISSIONS.CAMPAIGN_READ_BULK_MESSAGES) && (
                    <Route
                        exact
                        path={ROUTES.BULK_CONVERSATION}
                        render={() => <BulkConversationScreen />}
                    />
                )}
                {/* This route renders single bulk conversation page */}
                {checkPermission(PERMISSIONS.CAMPAIGN_READ_BULK_MESSAGES) && (
                    <Route path={ROUTES.SINGLE_BULK_CONVERSATION}>
                        <SingleBulkConversation />
                    </Route>
                )}
                {/* /comunications → redirect to bulk messages by default */}
                {checkPermission(PERMISSIONS.CAMPAIGN_ACCESS) && (
                    <Redirect exact from={ROUTES.COMMUNICATIONS} to={ROUTES.BULK_CONVERSATION} />
                )}
                {/* This route renders campaigns page*/}
                {checkPermission(PERMISSIONS.CAMPAIGN_ACCESS) && (
                    <Route path={ROUTES.CAMPAIGNS} render={() => <CampaignsScreen />} />
                )}
                {/* This route renders single campaign */}
                {checkPermission(PERMISSIONS.CAMPAIGN_READ) && (
                    <Route path={ROUTES.SINGLE_CAMPAIGN}>
                        <SingleCampaign />
                    </Route>
                )}
                {/* This route renders single activity page */}
                {checkPermission(PERMISSIONS.ACTIVITY_ACCESS) && (
                    <Route exact path={ROUTES.SINGLE_ACTIVITY}>
                        <SingleActivity />
                    </Route>
                )}
                {/* This route renders the list of all activities */}
                {checkPermission(PERMISSIONS.ACTIVITY_ACCESS) && (
                    <Route path={ROUTES.ACTIVITY} render={() => <ActivityScreen />} />
                )}
                {/* This route renders the list of all users */}
                {checkPermission(PERMISSIONS.USER_ACCESS) && (
                    <Route exact path={ROUTES.USERS} render={() => <UsersScreen />} />
                )}
                {/* This route renders the form to create a new user */}
                {checkPermission(PERMISSIONS.USER_CREATE) && (
                    <Route path={ROUTES.NEW_SINGLE_USERS}>
                        <CreateUser />
                    </Route>
                )}
                {checkPermission(PERMISSIONS.USER_READ) && (
                    <Route path={ROUTES.SINGLE_USERS}>
                        <SingleUser />
                    </Route>
                )}

                {/* This route renders the list of all leads */}
                {checkPermission(PERMISSIONS.LEAD_ACCESS) && (
                    <Route exact path={ROUTES.LEADS} render={() => <LeadsScreen />} />
                )}
                {checkPermission(PERMISSIONS.LEAD_DETAILS) && (
                    <Route path={ROUTES.LEADS_DETAILS} render={() => <LeadsDetailScreen />} />
                )}
                {checkPermission(PERMISSIONS.LEAD_ACCESS) && (
                    <Route path={ROUTES.SINGLE_LEADS} render={() => <SingleLead />} />
                )}

                {/* The route renders a single lead in detail */}
                {/* 
                    The route for a single lead in detail is inside leads mainscreen 
                    because it needs some parametres from the table filtering
                */}
                {/* This route renders a form to create a new lead */}
                {checkPermission(PERMISSIONS.LEAD_CREATE) && (
                    <Route
                        path={ROUTES.NEW_SINGLE_LEADS}
                        render={() => (
                            <CreateNewLead
                                fetchUrl={API.CREATE_LEAD}
                                redirectUrl={ROUTES.SINGLE_LEADS}
                                response_key="lead"
                            />
                        )}
                    />
                )}
                {/* This route renders the list of all client */}

                {checkPermission(PERMISSIONS.CLIENT_ACCESS) && (
                    <Route exact path={ROUTES.CLIENTS} render={() => <ClientsScreen />} />
                )}
                {checkPermission(PERMISSIONS.CLIENT_DETAILS) && (
                    <Route path={ROUTES.CLIENTS_DETAILS} render={() => <ClientsDetailScreen />} />
                )}
                {checkPermission(PERMISSIONS.CLIENT_ACTIVITY) && (
                    <Route path={ROUTES.CLIENTS_ACTIVITY} render={() => <ActivitySupervision />} />
                )}
                {checkPermission(PERMISSIONS.CLIENT_PRODUCTION) && (
                    <Route path={ROUTES.CLIENTS_PRODUCTION} render={() => <ProductionAudit />} />
                )}
                {checkPermission(PERMISSIONS.READ_PROPERTIES) && (
                    <Route exact path={ROUTES.REFINANCE_CASH_OUT} render={() => <RefinanceCashOutContainer />} />
                )}
                {checkPermission(PERMISSIONS.CLIENT_ACCESS) && (
                    <Route path={ROUTES.SINGLE_CLIENTS} render={() => <SingleClient />} />
                )}
                {/* 
                    The route for a single client in detail is inside clients mainscreen 
                    because it needs some parametres from the table filtering
                */}
                {/* This route renders a form to create a new client */}
                {checkPermission(PERMISSIONS.CLIENT_CREATE) && (
                    <Route
                        path={ROUTES.NEW_SINGLE_CLIENTS}
                        render={() => (
                            <CreateNewClient
                                fetchUrl={API.CREATE_CLIENT}
                                redirectUrl={ROUTES.SINGLE_CLIENTS}
                                response_key="client"
                            />
                        )}
                    />
                )}
                {/* BUSINESSES */}
                {checkPermission(PERMISSIONS.BUSINESS_CREATE) && (
                    <Route path={ROUTES.NEW_SINGLE_BUSINESS} render={() => <CreateBusinessContainer />} />
                )}
                {checkPermission(PERMISSIONS.BUSINESS_ACCESS_TAB) && (
                    <Route exact path={ROUTES.BUSINESS} render={() => <BusinessesScreen />} />
                )}
                {checkPermission(PERMISSIONS.BUSINESS_ACCESS_TAB) && (
                    <Route path={ROUTES.SINGLE_BUSINESS} render={() => <SingleBusinessContainer />} />
                )}
                {/* OPPORTUNITIES */}
                {checkPermission(PERMISSIONS.OPPORTUNITY_ACCESS_TAB) && (
                    <Route exact path={ROUTES.BUYER_SELLER_OPPORTUNITIES} render={() => <OpportunitiesScreen type="Buyer & Seller" />} />
                )}
                {checkPermission(PERMISSIONS.OPPORTUNITY_ACCESS_TAB) && (
                    <Route exact path={ROUTES.CR_OPPORTUNITIES} render={() => <OpportunitiesScreen type="CR" />} />
                )}
                {checkPermission(PERMISSIONS.OPPORTUNITY_ACCESS_TAB) && (
                    <Route exact path={ROUTES.MORTGAGE_OPPORTUNITIES} render={() => <OpportunitiesScreen type="Mortgage" />} />
                )}
                {checkPermission(PERMISSIONS.OPPORTUNITY_ACCESS_TAB) && (
                    <Route exact path={ROUTES.HELOC_OPPORTUNITIES} render={() => <OpportunitiesScreen type="HELOC" />} />
                )}
                {/* EMPLOYEES */}
                {checkPermission(PERMISSIONS.EMPLOYEE_ACCESS) && (
                    <Route exact path={ROUTES.EMPLOYEES} render={() => <EmployeeScreen />} />
                )}
                {checkPermission(PERMISSIONS.EMPLOYEE_CREATE) && (
                    <Route
                        exact
                        path={ROUTES.NEW_SINGLE_EMPLOYEES}
                        render={() => <CreateNewEmployee />}
                    />
                )}
                {checkPermission(PERMISSIONS.EMPLOYEE_ACCESS) && (
                    <Route path={ROUTES.SINGLE_EMPLOYEES} render={() => <SingleEmployee />} />
                )}
                {/* This route renders the calender view */}
                {checkPermission(PERMISSIONS.CALENDAR_ACCESS) && (
                    <Route exact path={ROUTES.CALENDAR} render={() => <CalendarScreen />} />
                )}


                {(checkPermission(PERMISSIONS.READ_CONVERSATION) || checkPermission(PERMISSIONS.READ_CONVERSATION_OWN)) && (
                    <Route exact path={ROUTES.CONVERSATIONS} render={() => <ConversationScreen />} />
                )}

                {/* This route renders the analytics view */}
                {checkPermission(PERMISSIONS.ANALYTICS_ACCESS) && (
                    <Route exact path={ROUTES.ANALYTICS_CHAT} render={() => <AnalyticsScreen initialTab="chat" />} />
                )}
                {checkPermission(PERMISSIONS.ANALYTICS_ACCESS) && (
                    <Route path={ROUTES.ANALYTICS} render={() => <AnalyticsScreen />} />
                )}

                {/* This route renders the tutorials page */}
                {checkPermission(PERMISSIONS.TUTORIAL_ACCESS) && (
                    <Route exact path={ROUTES.TUTORIALS} render={() => <TutorialsScreen />} />
                )}

                {/* This route renders the payments page */}
                {checkPermission(PERMISSIONS.PAYMENTS_ACCESS) && (
                    <Route exact path={ROUTES.PAYMENTS} render={() => <PaymentsScreen />} />
                )}


                {/* Landing for /teamhub: send the user to the first section they can access */}
                {checkPermission(PERMISSIONS.TEAMHUB_ACCESS) && teamHubLanding && (
                    <Route exact path={ROUTES.TEAMHUB}>
                        <Redirect to={teamHubLanding} />
                    </Route>
                )}
                {checkPermission(PERMISSIONS.TEAMHUB_TEMPLATES_ACCESS) && (
                    <Route
                        path={ROUTES.TEAMHUB_TEMPLATES}
                        render={() => <TeamHubTemplatesScreen />}
                    />
                )}
                {checkPermission(PERMISSIONS.TEAMHUB_SCHEDULE_ACCESS) && (
                    <Route
                        path={ROUTES.TEAMHUB_SCHEDULE}
                        render={() => <TeamHubScheduleScreen />}
                    />
                )}
                {checkPermission(PERMISSIONS.TEAMHUB_TOOLS_ACCESS) && (
                    <Route
                        path={ROUTES.TEAMHUB_TOOLS}
                        render={() => <TeamHubToolsScreen />}
                    />
                )}
                {(checkPermission(PERMISSIONS.TEAMHUB_TICKETS_ACCESS) ||
                    checkPermission(PERMISSIONS.TEAMHUB_TICKETS_MANAGE)) && (
                    <Route
                        path={ROUTES.TEAMHUB_TICKETS}
                        render={() => <TeamHubTicketsScreen />}
                    />
                )}
                {/* This route renders the not found view. The route need to be the last one*/}
                <Route path={ROUTES.NOT_FOUND} render={() => <NotFound />} />
            </Switch>
        </Suspense>
    );
};
